<template>
  <ButtonSquare
    :title="$t('general__view__sensordata')"
    :imgSource="require('@/assets/sensor-icon.svg')"
    size="large"
    :activeState="activeState"
    :showLoadingSpinner="isLoadingSensordata"
    :hoverButtons="hoverButtons"
    :activeHoverButtonValue="currentSensordataType"
    @clicked="toggle"
    @onHoverButtonClick="changeSensordataType"
    @mouseenter="onHover"
  />
</template>

<script>
import ButtonSquare from '@/components/general/buttons/ButtonSquare/ButtonSquare.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import { MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { SET_IS_SENSORDATA_SHOWING, SET_SENSORDATA_TYPE } from '@/store/mutation-types'
import { useFeature } from '@/pinia/features'

const sensorDataTypeToRole = {
  temperature: 'FEAT_SENSOR-TEMPERATURE_READER',
  co2: 'FEAT_SENSOR-CO2_READER',
  humidity: 'FEAT_SENSOR-HUMIDITY_READER',
  presence: 'FEAT_SENSOR-PRESENCE_READER',
  flow: 'FEAT_SENSOR-FLOW_READER'
}

export default {
  name: 'SensorDataButton',
  components: {
    ButtonSquare
  },
  emits: ['SensorDataButtonHovered'],
  setup() {
    const { feature } = useFeature('home')
    return { feature }
  },
  data() {
    return {
      hoverButtons: [],
      updateSensor: null
    }
  },
  computed: {
    ...mapState(PLANVIEW_PREFIX, {
      currentSensordataType: (state) => state.currentSensordataType,
      isSensordataShowing: (state) => state.isSensordataShowing,
      isLoadingSensordata: (state) => state.isLoadingSensordata,
      sensordataTypesForSelectedLevel: (state) => state.sensordataTypesForSelectedLevel
    }),
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding
    }),
    ...mapState({
      featureAccess: (state) => state.featureAccess
    }),
    activeState() {
      if (this.hoverButtons.length) return this.isSensordataShowing ? 'active' : 'inactive'
      return 'deactivated'
    },
    acceptedSensordataTypes() {
      return (
        this.feature?.props?.acceptedSensordataTypes ?? [
          'co2',
          'flow',
          'humidity',
          'presence',
          'temperature'
        ]
      ).filter((type) => this.featureAccess.includes(sensorDataTypeToRole[type]))
    }
  },
  watch: {
    sensordataTypesForSelectedLevel: {
      immediate: true,
      handler() {
        this.hoverButtons = []
        if (!this.sensordataTypesForSelectedLevel.length) {
          this.hoverButtons = this.getExtraHoverButtons()
        } else {
          const buttons = this.getHoverButtons(this.sensordataTypesForSelectedLevel)
          const extraButtons = this.getExtraHoverButtons()
          this.hoverButtons = [...buttons, ...extraButtons]
        }
        if (this.currentSensordataType && this.isSensordataShowing) return
        const firstEnabledButton = this.hoverButtons.find((button) => !button.disabled)
        this.setSensordataType({ type: firstEnabledButton ? firstEnabledButton.emit : null })
      }
    }
  },
  unmounted() {
    this.setSensordataType({ type: null })
    clearInterval(this.updateSensor)
    this.updateSensor = null
  },
  methods: {
    ...mapMutations({
      setInfoMessage: 'setInfoMessage'
    }),
    ...mapMutations(PLANVIEW_PREFIX, {
      setIsSensordataShowing: SET_IS_SENSORDATA_SHOWING,
      setSensordataType: SET_SENSORDATA_TYPE
    }),
    ...mapActions(PLANVIEW_PREFIX, {
      loadSensorData: 'loadSensorData'
    }),
    onHover() {
      if (this.activeState !== 'deactivated') {
        this.$emit('SensorDataButtonHovered')
      }
    },
    changeSensordataType(type) {
      this.setIsSensordataShowing({ value: true })
      this.setSensordataType({ type })
      if (this.updateSensor == null) this.updateSensordata()
    },
    updateSensordata() {
      this.loadSensorData()
      this.updateSensor = setInterval(() => {
        this.loadSensorData().then(() =>
          this.setInfoMessage({
            type: 'success',
            message: this.$t('message__sensor-data-fetch-update')
          })
        )
      }, 120000)
    },
    toggle() {
      if (this.activeState === 'deactivated') return null
      if (this.activeState === 'inactive') this.updateSensordata()
      if (this.activeState === 'active') {
        clearInterval(this.updateSensor)
        this.updateSensor = null
      }
      this.setIsSensordataShowing({ value: !this.isSensordataShowing })
      if (!this.isCurrentTypeInHoverButtons()) {
        const firstEnabledButton = this.hoverButtons.find((button) => !button.disabled)
        this.setSensordataType({ type: firstEnabledButton ? firstEnabledButton.emit : null })
      }
    },
    getHoverButtons(sensordataTypes) {
      const hoverButtons = []
      sensordataTypes.forEach((type) => {
        const lowerCaseType = type.toLowerCase()
        if (!this.acceptedSensordataTypes.includes(lowerCaseType)) return
        const hoverButtonTitleAndEmit = {
          title: this.$t(`general__data__sensor-${lowerCaseType}`),
          emit: lowerCaseType
        }
        hoverButtons.push({
          title: hoverButtonTitleAndEmit.title,
          emit: hoverButtonTitleAndEmit.emit
        })
      })
      return hoverButtons
    },
    getExtraHoverButtons() {
      const hoverButtons = []
      // Adding hover alternatives specific for VE (not sure if necessary, since this and many features seems not to work anyway)
      if (this.selectedBuilding.id === '774289046017777664') {
        hoverButtons.push({
          title: this.$t('general__data__sensor-temperature'),
          emit: 'temperature'
        })
      }
      // Adding hover alternatives specific for ICA Enköping
      if (this.selectedBuilding.id === '709333890242998272') {
        hoverButtons.push({ title: 'Wifi-täckning [demo]', emit: 'wifi' })
      }
      // Adding hover alternatives specific for AWL
      if (this.selectedBuilding.id === '834089140215631872') {
        hoverButtons.push(
          { title: 'Bokningsbara rum', emit: 'booking', disabled: true },
          { title: 'Wifi-täckning [demo]', emit: 'wifi' }
          // { title: 'Luftflöde', emit: 'flow' }
        )
      }
      return hoverButtons
    },
    isCurrentTypeInHoverButtons() {
      return this.hoverButtons.find((button) => button.emit === this.currentSensordataType)
    }
  }
}
</script>
